import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Button } from '../../components/ui/button';
import { getInitials, formatCurrency } from '../../lib/utils';
import { calculateTDSClient } from './payrollUtils';
import { Input } from '../../components/ui/input';
import {
  ArrowLeft, Loader2, ChevronRight, Download, IndianRupee,
  FileText, Shield, Landmark, Calendar, Pencil, Save, X, Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getTypeStyle(type) {
  const styles = {
    full_time: { label: 'Full-Time', color: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
    part_time: { label: 'Part-Time', color: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
    contractor: { label: 'Contractor', color: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
    freelancer: { label: 'Freelancer', color: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
    intern: { label: 'Intern', color: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' },
  };
  return styles[type] || styles.full_time;
}

const EMPLOYMENT_TYPES = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'freelancer', label: 'Freelancer' },
];
const PAY_TYPES = [{ value: 'salaried', label: 'Salaried' }, { value: 'hourly', label: 'Hourly' }];
const PAY_SCHEDULES = [
  { value: 'weekly', label: 'Weekly' }, { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'semimonthly', label: 'Semi-monthly' }, { value: 'monthly', label: 'Monthly' },
];

export default function PayrollEmployees({ employees, token, onDownloadPayslip, onEmployeeUpdated, salaryTemplates = [] }) {
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [empPayslips, setEmpPayslips] = useState(null);
  const [empYtd, setEmpYtd] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailTab, setDetailTab] = useState('compensation');
  const [expandedSlip, setExpandedSlip] = useState(null);

  // Edit states
  const [editingPay, setEditingPay] = useState(false);
  const [payForm, setPayForm] = useState({});
  const [savingPay, setSavingPay] = useState(false);
  const [editingBank, setEditingBank] = useState(false);
  const [bankForm, setBankForm] = useState({});
  const [savingBank, setSavingBank] = useState(false);

  const fetchEmpPayslips = useCallback(async (empId) => {
    setLoadingDetail(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/payroll/employees/${empId}/payslips`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEmpPayslips(data.payslips);
        setEmpYtd(data.ytd);
      }
    } catch (e) { console.error(e); } finally { setLoadingDetail(false); }
  }, [token]);

  const handleSelectEmp = (emp) => {
    setSelectedEmp(emp);
    setDetailTab('compensation');
    setExpandedSlip(null);
    fetchEmpPayslips(emp.id);
  };

  const handleBack = () => {
    setSelectedEmp(null);
    setEmpPayslips(null);
    setEmpYtd(null);
    setEditingPay(false);
    setEditingBank(false);
  };

  // Pay info edit handlers
  const startEditPay = () => {
    setPayForm({
      employment_type: selectedEmp.employment_type,
      pay_type: selectedEmp.pay_type,
      pay_rate: selectedEmp.pay_rate,
      pay_currency: selectedEmp.pay_currency || 'INR',
      pay_schedule: selectedEmp.pay_schedule,
      weekly_hours_target: selectedEmp.weekly_hours_target || 40,
      overtime_eligible: selectedEmp.overtime_eligible || false,
    });
    setEditingPay(true);
  };

  const savePay = async () => {
    setSavingPay(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/payroll/employees/${selectedEmp.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payForm)
      });
      if (res.ok) {
        const updated = { ...selectedEmp, ...payForm };
        setSelectedEmp(updated);
        setEditingPay(false);
        if (onEmployeeUpdated) onEmployeeUpdated();
      }
    } catch (err) { console.error('Save pay error:', err); }
    finally { setSavingPay(false); }
  };

  // Bank details edit handlers
  const startEditBank = () => {
    setBankForm({
      bank_name: selectedEmp.bank_name || '',
      bank_branch: selectedEmp.bank_branch || '',
      bank_account_number: selectedEmp.bank_account_number || '',
      bank_ifsc_code: selectedEmp.bank_ifsc_code || '',
      pan_number: selectedEmp.pan_number || '',
      uan_number: selectedEmp.uan_number || '',
    });
    setEditingBank(true);
  };

  const saveBank = async () => {
    setSavingBank(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/payroll/employees/${selectedEmp.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(bankForm)
      });
      if (res.ok) {
        const updated = { ...selectedEmp, ...bankForm };
        setSelectedEmp(updated);
        setEditingBank(false);
        if (onEmployeeUpdated) onEmployeeUpdated();
      }
    } catch (err) { console.error('Save bank error:', err); }
    finally { setSavingBank(false); }
  };

  // Determine if employee is a contractor/freelancer
  const isContractorType = (emp) => emp?.employment_type === 'contractor' || emp?.employment_type === 'freelancer';

  // Find the contractor TDS rate from the assigned template (Section 194J = 10%, 194C = 2%)
  const getContractorTdsInfo = (emp) => {
    if (!isContractorType(emp) || !salaryTemplates?.length) return null;
    const tmpl = salaryTemplates.find(t => t.id === emp.salary_template_id)
      || salaryTemplates.find(t => t.name && t.name.toLowerCase().includes('contractor'));
    if (!tmpl) return null;
    const tdsComp = (tmpl.components || []).find(c =>
      c.category === 'deduction' && (c.code === 'tds' || (c.name && c.name.toLowerCase().includes('tds')))
    );
    if (!tdsComp) return null;
    return {
      templateName: tmpl.name,
      section: (tdsComp.name && tdsComp.name.match(/19[0-9][A-Z]/)?.[0]) || null,
      rate: tdsComp.type === 'percentage' ? tdsComp.value : 0,
      type: tdsComp.type,
      baseOn: tdsComp.base_on || 'gross',
    };
  };

  // Compute CTC info
  const getCTCInfo = (emp) => {
    const scheduleMap = { weekly: 52, biweekly: 26, semimonthly: 24, monthly: 12 };
    const periods = scheduleMap[emp.pay_schedule || 'monthly'] || 12;
    const monthlyCTC = emp.pay_type === 'salaried' ? emp.pay_rate : 0;
    const annualCTC = monthlyCTC * periods;

    // Contractors: TDS comes from the template (e.g., 10% of gross for 194J), NOT slab calc
    if (isContractorType(emp) && monthlyCTC > 0) {
      const cInfo = getContractorTdsInfo(emp);
      const rate = cInfo?.rate || 0;
      const monthlyTds = Math.round((monthlyCTC * rate) / 100);
      return { monthlyCTC, annualCTC, monthlyTds, contractorTds: cInfo };
    }

    const tds = emp.pay_type === 'salaried' && annualCTC > 0 ? calculateTDSClient(annualCTC) : { monthlyTds: 0 };
    return { monthlyCTC, annualCTC, monthlyTds: tds.monthlyTds, contractorTds: null };
  };

  // ==================== EMPLOYEE DETAIL VIEW ====================
  if (selectedEmp) {
    const ctc = getCTCInfo(selectedEmp);
    const typeStyle = getTypeStyle(selectedEmp.employment_type);
    const DETAIL_TABS = [
      { key: 'compensation', label: 'Compensation', icon: IndianRupee },
      { key: 'payslips', label: 'Payslip History', icon: FileText },
      { key: 'tax', label: 'Tax Sheet', icon: Shield },
      { key: 'bank', label: 'Bank & Personal', icon: Landmark },
    ];

    return (
      <div className="space-y-4" data-testid="emp-detail-view">
        <button onClick={handleBack} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors" data-testid="emp-detail-back">
          <ArrowLeft className="w-4 h-4" /> Back to Employees
        </button>

        {/* Employee Header */}
        <Card className="border-slate-200/60 dark:border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <Avatar className="h-12 w-12">
                {selectedEmp.profile_photo && <AvatarImage src={selectedEmp.profile_photo} />}
                <AvatarFallback className="bg-slate-100 dark:bg-slate-700 text-slate-600 font-semibold">
                  {getInitials(selectedEmp.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">{selectedEmp.name}</h2>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-slate-500">{selectedEmp.department || selectedEmp.job_title || selectedEmp.role}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${typeStyle.color}`}>{typeStyle.label}</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-400">Monthly CTC</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white" data-testid="emp-monthly-ctc">{formatCurrency(ctc.monthlyCTC, selectedEmp.pay_currency)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-400">Annual CTC</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white" data-testid="emp-annual-ctc">{formatCurrency(ctc.annualCTC, selectedEmp.pay_currency)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Detail Tabs */}
        <div className="flex gap-1 border-b border-slate-200 dark:border-slate-700">
          {DETAIL_TABS.map(t => (
            <button key={t.key} onClick={() => setDetailTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                detailTab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`} data-testid={`emp-tab-${t.key}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {loadingDetail && <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>}

        {/* Compensation Tab */}
        {detailTab === 'compensation' && !loadingDetail && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="emp-compensation-tab">
            <Card className="border-slate-200/60">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Pay Structure</CardTitle>
                  {!editingPay ? (
                    <Button variant="ghost" size="sm" onClick={startEditPay} className="h-7 text-xs gap-1 text-slate-500 hover:text-blue-600" data-testid="edit-pay-btn">
                      <Pencil className="w-3 h-3" /> Edit
                    </Button>
                  ) : (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={savePay} disabled={savingPay} className="h-7 text-xs gap-1 text-emerald-600 hover:text-emerald-700" data-testid="save-pay-btn">
                        {savingPay ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditingPay(false)} className="h-7 text-xs text-slate-400" data-testid="cancel-pay-btn">
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-2.5">
                {editingPay ? (
                  <>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500">Employment Type</span>
                      <select value={payForm.employment_type} onChange={e => setPayForm(p => ({...p, employment_type: e.target.value}))}
                        className="text-xs px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 w-36" data-testid="edit-employment-type">
                        {EMPLOYMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500">Pay Type</span>
                      <select value={payForm.pay_type} onChange={e => setPayForm(p => ({...p, pay_type: e.target.value}))}
                        className="text-xs px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 w-36" data-testid="edit-pay-type">
                        {PAY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500">Pay Rate</span>
                      <div className="flex items-center gap-1">
                        <select value={payForm.pay_currency} onChange={e => setPayForm(p => ({...p, pay_currency: e.target.value}))}
                          className="text-xs px-1 py-1.5 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 w-16" data-testid="edit-currency">
                          <option value="INR">INR</option><option value="USD">USD</option><option value="EUR">EUR</option><option value="GBP">GBP</option>
                        </select>
                        <Input type="number" value={payForm.pay_rate} onChange={e => setPayForm(p => ({...p, pay_rate: parseFloat(e.target.value) || 0}))}
                          className="w-28 h-7 text-xs text-right" data-testid="edit-pay-rate" />
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500">Pay Schedule</span>
                      <select value={payForm.pay_schedule} onChange={e => setPayForm(p => ({...p, pay_schedule: e.target.value}))}
                        className="text-xs px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 w-36" data-testid="edit-pay-schedule">
                        {PAY_SCHEDULES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500">Weekly Hours</span>
                      <Input type="number" value={payForm.weekly_hours_target} onChange={e => setPayForm(p => ({...p, weekly_hours_target: parseInt(e.target.value) || 40}))}
                        className="w-20 h-7 text-xs text-center" data-testid="edit-weekly-hours" />
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500">Overtime Eligible</span>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={payForm.overtime_eligible} onChange={e => setPayForm(p => ({...p, overtime_eligible: e.target.checked}))}
                          className="w-4 h-4 rounded border-slate-300" data-testid="edit-overtime" />
                        <span className="text-xs">{payForm.overtime_eligible ? 'Yes' : 'No'}</span>
                      </label>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between text-xs"><span className="text-slate-500">Employment Type</span><span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${typeStyle.color}`}>{typeStyle.label}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-slate-500">Pay Type</span><span className="font-medium capitalize">{selectedEmp.pay_type}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-slate-500">Pay Schedule</span><span className="font-medium capitalize">{selectedEmp.pay_schedule}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-slate-500">Rate per Period</span><span className="font-bold">{formatCurrency(selectedEmp.pay_rate, selectedEmp.pay_currency)}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-slate-500">Monthly CTC</span><span className="font-bold text-emerald-600">{formatCurrency(ctc.monthlyCTC, selectedEmp.pay_currency)}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-slate-500">Annual CTC</span><span className="font-bold text-emerald-600">{formatCurrency(ctc.annualCTC, selectedEmp.pay_currency)}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-slate-500">Weekly Hours</span><span className="font-medium">{selectedEmp.weekly_hours_target}h</span></div>
                    <div className="flex justify-between text-xs"><span className="text-slate-500">Overtime Eligible</span><span className="font-medium">{selectedEmp.overtime_eligible ? 'Yes' : 'No'}</span></div>
                    {selectedEmp.salary_template_name && <div className="flex justify-between text-xs"><span className="text-slate-500">Salary Template</span><span className="font-medium">{selectedEmp.salary_template_name}</span></div>}
                  </>
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-200/60">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Monthly Deductions (Est.)</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between text-xs"><span className="text-slate-500">TDS (Income Tax)</span><span className="font-medium text-red-500">-{formatCurrency(ctc.monthlyTds, selectedEmp.pay_currency)}</span></div>
                <div className="flex justify-between text-xs"><span className="text-slate-500">Estimated Net Pay</span><span className="font-bold text-lg text-slate-900 dark:text-white">{formatCurrency(ctc.monthlyCTC - ctc.monthlyTds, selectedEmp.pay_currency)}</span></div>
                <p className="text-[10px] text-slate-400 mt-2">*Estimated. Actual may vary based on PF, PT, adjustments, and LOP.</p>
              </CardContent>
            </Card>

            {empYtd && (
              <Card className="border-slate-200/60 lg:col-span-2">
                <CardHeader className="pb-2"><CardTitle className="text-sm">YTD Summary (Current FY)</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                      <p className="text-[10px] text-emerald-600 font-medium uppercase">YTD Earnings</p>
                      <p className="text-lg font-bold text-emerald-700" data-testid="emp-ytd-earnings">{formatCurrency(empYtd.earnings, selectedEmp.pay_currency)}</p>
                    </div>
                    <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <p className="text-[10px] text-red-600 font-medium uppercase">YTD TDS</p>
                      <p className="text-lg font-bold text-red-700" data-testid="emp-ytd-tds">{formatCurrency(empYtd.tds, selectedEmp.pay_currency)}</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-[10px] text-blue-600 font-medium uppercase">YTD PF</p>
                      <p className="text-lg font-bold text-blue-700">{formatCurrency(empYtd.pf, selectedEmp.pay_currency)}</p>
                    </div>
                    <div className="text-center p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                      <p className="text-[10px] text-amber-600 font-medium uppercase">YTD PT</p>
                      <p className="text-lg font-bold text-amber-700">{formatCurrency(empYtd.pt, selectedEmp.pay_currency)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Payslip History Tab */}
        {detailTab === 'payslips' && !loadingDetail && (
          <Card className="border-slate-200/60" data-testid="emp-payslips-tab">
            <CardContent className="p-0">
              {(!empPayslips || empPayslips.length === 0) ? (
                <div className="text-center py-12 text-sm text-slate-400">No payslips generated yet</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs font-semibold uppercase">Period</TableHead>
                      <TableHead className="text-xs font-semibold uppercase text-right">Gross</TableHead>
                      <TableHead className="text-xs font-semibold uppercase text-right">Deductions</TableHead>
                      <TableHead className="text-xs font-semibold uppercase text-right">Net Pay</TableHead>
                      <TableHead className="text-xs font-semibold uppercase text-center">Status</TableHead>
                      <TableHead className="text-xs font-semibold uppercase w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {empPayslips.map((slip, i) => {
                      const isExpanded = expandedSlip === i;
                      const earnings = (slip.components || []).filter(c => c.category === 'earning');
                      const deductions = (slip.components || []).filter(c => c.category === 'deduction');
                      return (
                        <React.Fragment key={i}>
                          <TableRow className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800" onClick={() => setExpandedSlip(isExpanded ? null : i)} data-testid={`emp-slip-${i}`}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                <span className="text-sm font-medium">{new Date(slip.period_start).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right text-sm font-medium">{formatCurrency(slip.gross_pay, slip.currency)}</TableCell>
                            <TableCell className="text-right text-sm text-red-500">-{formatCurrency(slip.total_deductions, slip.currency)}</TableCell>
                            <TableCell className="text-right text-sm font-bold">{formatCurrency(slip.net_pay, slip.currency)}</TableCell>
                            <TableCell className="text-center">
                              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
                                slip.status === 'processed' ? 'bg-emerald-100 text-emerald-700' :
                                slip.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                                'bg-slate-100 text-slate-500'
                              }`}>{slip.status}</span>
                            </TableCell>
                            <TableCell>
                              <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                            </TableCell>
                          </TableRow>
                          {isExpanded && (
                            <TableRow>
                              <TableCell colSpan={6} className="bg-slate-50/80 dark:bg-slate-800/50 p-4">
                                <div className="grid grid-cols-2 gap-6">
                                  <div>
                                    <p className="text-[10px] font-bold text-emerald-600 uppercase mb-1.5">Earnings</p>
                                    {earnings.map((c, j) => (
                                      <div key={j} className="flex justify-between text-xs py-0.5">
                                        <span className="text-slate-600">{c.name} {c.is_adjustment && <span className="text-blue-500 text-[9px]">(Adj)</span>}</span>
                                        <span className="font-mono font-medium">+{formatCurrency(c.amount, slip.currency)}</span>
                                      </div>
                                    ))}
                                    <div className="border-t mt-1.5 pt-1.5 flex justify-between text-xs font-bold">
                                      <span>Gross Pay</span><span>{formatCurrency(slip.gross_pay, slip.currency)}</span>
                                    </div>
                                  </div>
                                  <div>
                                    <p className="text-[10px] font-bold text-red-500 uppercase mb-1.5">Deductions</p>
                                    {deductions.length === 0 && <p className="text-xs text-slate-400 italic">None</p>}
                                    {deductions.map((c, j) => (
                                      <div key={j} className="flex justify-between text-xs py-0.5">
                                        <span className="text-slate-600">{c.name} {c.is_adjustment && <span className="text-blue-500 text-[9px]">(Adj)</span>}</span>
                                        <span className="font-mono font-medium text-red-500">-{formatCurrency(c.amount, slip.currency)}</span>
                                      </div>
                                    ))}
                                    <div className="border-t mt-1.5 pt-1.5 flex justify-between text-xs font-bold">
                                      <span>Total Deductions</span><span className="text-red-600">-{formatCurrency(slip.total_deductions, slip.currency)}</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="border-t mt-3 pt-3 flex justify-between items-center">
                                  <span className="text-sm font-bold">Net Pay</span>
                                  <span className="text-lg font-bold text-emerald-600">{formatCurrency(slip.net_pay, slip.currency)}</span>
                                </div>
                                {slip.lop_days > 0 && <p className="text-[10px] text-amber-600 mt-1">LOP: {slip.lop_days} day(s)</p>}
                                {slip.adjustments !== 0 && <p className="text-[10px] text-blue-600 mt-1">Adjustments: {formatCurrency(slip.adjustments, slip.currency)}</p>}
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tax Sheet Tab */}
        {detailTab === 'tax' && !loadingDetail && (
          <div className="space-y-4" data-testid="emp-tax-tab">
            {/* CONTRACTOR / FREELANCER VIEW: Template-based TDS (Section 194J / 194C) */}
            {ctc.annualCTC > 0 && selectedEmp.pay_type === 'salaried' && isContractorType(selectedEmp) ? (
              <Card className="border-amber-200/60 bg-amber-50/30" data-testid="contractor-tax-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Shield className="w-4 h-4 text-amber-600" />
                    Contractor TDS {ctc.contractorTds?.section ? `(Section ${ctc.contractorTds.section})` : ''}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="bg-amber-100/60 p-2 rounded text-[11px] text-amber-800 border border-amber-200">
                    Contractors &amp; freelancers are taxed at a flat rate per their assigned template — Indian income-tax slabs do not apply.
                  </div>
                  <div className="flex justify-between text-xs"><span className="text-slate-500">Template</span><span className="font-medium">{ctc.contractorTds?.templateName || selectedEmp.salary_template_name || '—'}</span></div>
                  <div className="flex justify-between text-xs"><span className="text-slate-500">TDS Rate</span><span className="font-bold text-amber-700">{ctc.contractorTds?.rate ?? 0}% of {ctc.contractorTds?.baseOn === 'basic' ? 'Basic' : 'Gross'}</span></div>
                  <div className="flex justify-between text-xs border-t pt-1"><span className="text-slate-500">Monthly Gross</span><span className="font-medium">{formatCurrency(ctc.monthlyCTC, selectedEmp.pay_currency)}</span></div>
                  <div className="flex justify-between text-xs"><span className="text-slate-500">Annual Gross</span><span className="font-medium">{formatCurrency(ctc.annualCTC, selectedEmp.pay_currency)}</span></div>
                  <div className="flex justify-between text-sm border-t pt-2 font-bold"><span>Monthly TDS</span><span className="text-red-600 text-lg" data-testid="contractor-monthly-tds">{formatCurrency(ctc.monthlyTds, selectedEmp.pay_currency)}</span></div>
                  <div className="flex justify-between text-xs"><span className="text-slate-500">Annual TDS (est.)</span><span className="font-medium text-red-500">{formatCurrency(ctc.monthlyTds * 12, selectedEmp.pay_currency)}</span></div>
                  {!ctc.contractorTds && (
                    <div className="bg-orange-50 border border-orange-200 p-2 rounded text-[11px] text-orange-700 mt-2">
                      No contractor TDS template assigned. Assign a template with a TDS component (e.g., Section 194J – 10%) from Payroll → Salary Structure.
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : ctc.annualCTC > 0 && selectedEmp.pay_type === 'salaried' ? (() => {
              const tds = calculateTDSClient(ctc.annualCTC);
              return (
                <>
                  <Card className="border-slate-200/60">
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Tax Calculation (FY 2025-26 New Regime)</CardTitle></CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex justify-between text-xs"><span className="text-slate-500">Annual Gross</span><span className="font-medium">{formatCurrency(ctc.annualCTC, selectedEmp.pay_currency)}</span></div>
                      <div className="flex justify-between text-xs"><span className="text-slate-500">Standard Deduction</span><span className="font-medium text-red-500">-{formatCurrency(tds.standardDeduction, selectedEmp.pay_currency)}</span></div>
                      <div className="flex justify-between text-xs border-t pt-1"><span className="text-slate-500">Taxable Income</span><span className="font-bold">{formatCurrency(tds.taxableIncome, selectedEmp.pay_currency)}</span></div>
                      {tds.rebateApplied && <div className="bg-green-50 p-2 rounded text-[11px] text-green-700 font-medium">Rebate u/s 87A applied — No tax payable</div>}
                      {!tds.rebateApplied && (
                        <>
                          <div className="border-t pt-2 mt-2">
                            <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Slab Breakdown</p>
                            {tds.slabBreakdown.map((s, i) => (
                              <div key={i} className="flex justify-between text-xs py-0.5">
                                <span className="text-slate-500">{s.range} @ {s.rate}%</span>
                                <span className="font-mono">{formatCurrency(s.tax, selectedEmp.pay_currency)}</span>
                              </div>
                            ))}
                          </div>
                          <div className="flex justify-between text-xs"><span className="text-slate-500">Cess (4%)</span><span className="font-mono">{formatCurrency(tds.cess, selectedEmp.pay_currency)}</span></div>
                          <div className="flex justify-between text-xs border-t pt-1 font-bold"><span>Annual Tax</span><span className="text-red-600">{formatCurrency(tds.annualTax, selectedEmp.pay_currency)}</span></div>
                          <div className="flex justify-between text-sm border-t pt-2 font-bold"><span>Monthly TDS</span><span className="text-red-600 text-lg">{formatCurrency(tds.monthlyTds, selectedEmp.pay_currency)}</span></div>
                        </>
                      )}
                    </CardContent>
                  </Card>

                  {/* Month-wise TDS from payslips */}
                  {empPayslips && empPayslips.length > 0 && (
                    <Card className="border-slate-200/60">
                      <CardHeader className="pb-2"><CardTitle className="text-sm">Month-wise TDS Deducted</CardTitle></CardHeader>
                      <CardContent className="p-0">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-xs font-semibold uppercase">Month</TableHead>
                              <TableHead className="text-xs font-semibold uppercase text-right">Gross</TableHead>
                              <TableHead className="text-xs font-semibold uppercase text-right">TDS</TableHead>
                              <TableHead className="text-xs font-semibold uppercase text-right">PF</TableHead>
                              <TableHead className="text-xs font-semibold uppercase text-right">PT</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {empPayslips.filter(s => s.status === 'approved' || s.status === 'processed').map((slip, i) => {
                              const comps = slip.components || [];
                              const tdsAmt = comps.find(c => c.code === 'tds')?.amount || 0;
                              const pfAmt = comps.find(c => c.code === 'pf_employee')?.amount || 0;
                              const ptAmt = comps.find(c => c.code === 'pt')?.amount || 0;
                              return (
                                <TableRow key={i}>
                                  <TableCell className="text-xs font-medium">{new Date(slip.period_start).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}</TableCell>
                                  <TableCell className="text-xs text-right">{formatCurrency(slip.gross_pay, slip.currency)}</TableCell>
                                  <TableCell className="text-xs text-right text-red-500">{tdsAmt > 0 ? formatCurrency(tdsAmt, slip.currency) : '—'}</TableCell>
                                  <TableCell className="text-xs text-right">{pfAmt > 0 ? formatCurrency(pfAmt, slip.currency) : '—'}</TableCell>
                                  <TableCell className="text-xs text-right">{ptAmt > 0 ? formatCurrency(ptAmt, slip.currency) : '—'}</TableCell>
                                </TableRow>
                              );
                            })}
                            {empYtd && (
                              <TableRow className="bg-slate-50 dark:bg-slate-800 font-bold">
                                <TableCell className="text-xs">YTD Total</TableCell>
                                <TableCell className="text-xs text-right">{formatCurrency(empYtd.earnings, selectedEmp.pay_currency)}</TableCell>
                                <TableCell className="text-xs text-right text-red-600">{formatCurrency(empYtd.tds, selectedEmp.pay_currency)}</TableCell>
                                <TableCell className="text-xs text-right">{formatCurrency(empYtd.pf, selectedEmp.pay_currency)}</TableCell>
                                <TableCell className="text-xs text-right">{formatCurrency(empYtd.pt, selectedEmp.pay_currency)}</TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}
                </>
              );
            })() : (
              <Card className="border-slate-200/60"><CardContent className="py-12 text-center text-sm text-slate-400">Tax calculation not applicable for {selectedEmp.pay_type} employees</CardContent></Card>
            )}
          </div>
        )}

        {/* Bank & Personal Tab */}
        {detailTab === 'bank' && !loadingDetail && (
          <div className="space-y-4" data-testid="emp-bank-tab">
            <div className="flex justify-end">
              {!editingBank ? (
                <Button variant="outline" size="sm" onClick={startEditBank} className="h-7 text-xs gap-1" data-testid="edit-bank-btn">
                  <Pencil className="w-3 h-3" /> Edit Details
                </Button>
              ) : (
                <div className="flex gap-1.5">
                  <Button size="sm" onClick={saveBank} disabled={savingBank} className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-bank-btn">
                    {savingBank ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setEditingBank(false)} className="h-7 text-xs" data-testid="cancel-bank-btn">
                    Cancel
                  </Button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="border-slate-200/60">
                <CardHeader className="pb-2"><CardTitle className="text-sm">Bank Details</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {editingBank ? (
                    <>
                      <div>
                        <label className="block text-[10px] font-medium text-slate-500 uppercase mb-1">Bank Name</label>
                        <Input value={bankForm.bank_name} onChange={e => setBankForm(p => ({...p, bank_name: e.target.value}))}
                          placeholder="e.g. HDFC Bank" className="h-8 text-sm" data-testid="edit-bank-name" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-slate-500 uppercase mb-1">Branch</label>
                        <Input value={bankForm.bank_branch} onChange={e => setBankForm(p => ({...p, bank_branch: e.target.value}))}
                          placeholder="e.g. Koramangala" className="h-8 text-sm" data-testid="edit-bank-branch" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-slate-500 uppercase mb-1">Account Number</label>
                        <Input value={bankForm.bank_account_number} onChange={e => setBankForm(p => ({...p, bank_account_number: e.target.value}))}
                          placeholder="e.g. 1234567890123" className="h-8 text-sm font-mono" data-testid="edit-bank-account" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-slate-500 uppercase mb-1">IFSC Code</label>
                        <Input value={bankForm.bank_ifsc_code} onChange={e => setBankForm(p => ({...p, bank_ifsc_code: e.target.value.toUpperCase()}))}
                          placeholder="e.g. HDFC0001234" className="h-8 text-sm font-mono" data-testid="edit-bank-ifsc" />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between text-xs"><span className="text-slate-500">Bank Name</span><span className="font-medium">{selectedEmp.bank_name || '—'}</span></div>
                      <div className="flex justify-between text-xs"><span className="text-slate-500">Branch</span><span className="font-medium">{selectedEmp.bank_branch || '—'}</span></div>
                      <div className="flex justify-between text-xs"><span className="text-slate-500">Account Number</span><span className="font-mono font-medium">{selectedEmp.bank_account_number || '—'}</span></div>
                      <div className="flex justify-between text-xs"><span className="text-slate-500">IFSC Code</span><span className="font-mono font-medium">{selectedEmp.bank_ifsc_code || '—'}</span></div>
                    </>
                  )}
                </CardContent>
              </Card>
              <Card className="border-slate-200/60">
                <CardHeader className="pb-2"><CardTitle className="text-sm">Tax & Statutory IDs</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {editingBank ? (
                    <>
                      <div>
                        <label className="block text-[10px] font-medium text-slate-500 uppercase mb-1">PAN Number</label>
                        <Input value={bankForm.pan_number} onChange={e => setBankForm(p => ({...p, pan_number: e.target.value.toUpperCase()}))}
                          placeholder="e.g. ABCDE1234F" maxLength={10} className="h-8 text-sm font-mono" data-testid="edit-pan" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-slate-500 uppercase mb-1">UAN (PF Account)</label>
                        <Input value={bankForm.uan_number} onChange={e => setBankForm(p => ({...p, uan_number: e.target.value}))}
                          placeholder="e.g. 100012345678" className="h-8 text-sm font-mono" data-testid="edit-uan" />
                      </div>
                      <div className="flex justify-between text-xs pt-1"><span className="text-slate-500">Email</span><span className="font-medium">{selectedEmp.email}</span></div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between text-xs"><span className="text-slate-500">PAN Number</span><span className="font-mono font-medium">{selectedEmp.pan_number || '—'}</span></div>
                      <div className="flex justify-between text-xs"><span className="text-slate-500">UAN (PF Account)</span><span className="font-mono font-medium">{selectedEmp.uan_number || '—'}</span></div>
                      <div className="flex justify-between text-xs"><span className="text-slate-500">Email</span><span className="font-medium">{selectedEmp.email}</span></div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ==================== EMPLOYEE LIST VIEW ====================
  return (
    <Card className="border-slate-200/60 dark:border-slate-700" data-testid="payroll-employees">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-200 dark:border-slate-700">
              <TableHead className="text-xs font-semibold uppercase tracking-wider">Employee</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider">Type</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">Monthly CTC</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">Annual CTC</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">TDS/Month</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-center">Schedule</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider w-8"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {employees.map(emp => {
              const ctc = getCTCInfo(emp);
              const typeStyle = getTypeStyle(emp.employment_type);
              return (
                <TableRow key={emp.id} className="border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800"
                  onClick={() => handleSelectEmp(emp)} data-testid={`payroll-emp-${emp.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <Avatar className="h-8 w-8">
                        {emp.profile_photo && <AvatarImage src={emp.profile_photo} />}
                        <AvatarFallback className="bg-slate-100 dark:bg-slate-700 text-slate-600 text-xs font-semibold">
                          {getInitials(emp.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-semibold text-slate-900 dark:text-white">{emp.name}</p>
                        <p className="text-xs text-slate-400">{emp.department || emp.role}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${typeStyle.color}`}>{typeStyle.label}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="text-sm font-bold text-slate-900 dark:text-white">{ctc.monthlyCTC > 0 ? formatCurrency(ctc.monthlyCTC, emp.pay_currency) : '—'}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="text-sm text-slate-600 dark:text-slate-400">{ctc.annualCTC > 0 ? formatCurrency(ctc.annualCTC, emp.pay_currency) : '—'}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="text-sm text-red-500">{ctc.monthlyTds > 0 ? formatCurrency(ctc.monthlyTds, emp.pay_currency) : '—'}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="text-xs text-slate-500 capitalize">{emp.pay_schedule}</span>
                  </TableCell>
                  <TableCell>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
